import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ChevronDown as ChevronDownIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../../../../components/ui/popover/index.js";
import { cn } from "../../../../lib/utils.js";
import { TechnicalValue } from "../../TechnicalValue.js";
import {
  buildTemplateLanguageOptions,
  filterLanguageOptions,
} from "../template-language-options.js";
import {
  findLanguageLabel,
  initialLanguageSelectUiState,
  reduceLanguageSelectUi,
} from "../template-language-select-state.js";

export interface TemplateLanguageSelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  /** Force English language names (Meta WhatsApp Manager). */
  englishOnly?: boolean;
  /** Hide the technical language code from the trigger and list. */
  hideCodes?: boolean;
  /** Circular radio indicator instead of code + highlight. */
  radioSelection?: boolean;
  /** Compact Meta-like control (narrower trigger, smaller type). */
  compact?: boolean;
}

export function TemplateLanguageSelect({
  value,
  onChange,
  disabled = false,
  englishOnly = false,
  hideCodes = false,
  radioSelection = false,
  compact = false,
}: TemplateLanguageSelectProps): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [uiState, setUiState] = useState(initialLanguageSelectUiState);

  const options = useMemo(
    () =>
      buildTemplateLanguageOptions(i18n.language, {
        englishOnly,
        hideCodes,
      }),
    [i18n.language, englishOnly, hideCodes],
  );
  const filtered = useMemo(
    () => filterLanguageOptions(options, uiState.query),
    [options, uiState.query],
  );
  const selectedLabel = findLanguageLabel(options, value);

  function selectLanguage(code: string): void {
    onChange(code);
    setUiState((current) => reduceLanguageSelectUi(current, { type: "select" }));
  }

  return (
    <Popover
      open={disabled ? false : uiState.open}
      onOpenChange={(open) => {
        if (disabled) return;
        setUiState((current) =>
          reduceLanguageSelectUi(current, { type: open ? "open" : "close" }),
        );
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-sm border border-border bg-surface text-start text-inherit disabled:cursor-not-allowed disabled:opacity-65",
            compact ? "h-9 px-2.5 text-[0.8125rem]" : "px-3 py-2",
          )}
          aria-haspopup="listbox"
          aria-expanded={uiState.open}
          disabled={disabled}
        >
          <span className="min-w-0 flex-1 truncate">{selectedLabel}</span>
          {!hideCodes ? (
            <TechnicalValue className="shrink-0 text-[0.8rem] text-muted">{value}</TechnicalValue>
          ) : null}
          <ChevronDownIcon size={16} className="shrink-0 opacity-70" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={4}
        className="w-[min(24rem,calc(100vw-1.5rem))] min-w-[18rem] rounded-md border-border p-3"
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          searchInputRef.current?.focus();
        }}
      >
        <div className="grid gap-2">
          <input
            ref={searchInputRef}
            type="search"
            className={cn(
              "w-full rounded-sm border border-border bg-surface px-3 text-start",
              compact ? "h-8 py-1.5 text-xs" : "py-2",
            )}
            value={uiState.query}
            onChange={(event) =>
              setUiState((current) =>
                reduceLanguageSelectUi(current, {
                  type: "setQuery",
                  query: event.target.value,
                }),
              )
            }
            placeholder={t("whatsapp.templates.manage.languageSearch")}
            disabled={disabled}
            aria-label={t("whatsapp.templates.manage.languageSearch")}
          />
          <ul
            className="m-0 max-h-[min(18rem,50vh)] list-none overflow-y-auto rounded-sm border border-border bg-surface p-0"
            role="listbox"
            aria-label={t("whatsapp.templates.manage.language")}
          >
            {filtered.map((option) => {
              const selected = option.code === value;
              return (
                <li key={option.code}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={selected}
                    className={cn(
                      "flex w-full cursor-pointer items-center gap-2 border-0 bg-transparent px-3 text-start text-inherit not-disabled:hover:bg-accent/10",
                      compact ? "py-1.5 text-[0.8125rem]" : "py-2",
                      selected && !radioSelection && "bg-accent/10 font-semibold",
                    )}
                    onClick={() => selectLanguage(option.code)}
                    disabled={disabled}
                  >
                    {radioSelection ? (
                      <span
                        className={cn(
                          "size-4 shrink-0 rounded-full border",
                          selected
                            ? "border-[4.5px] border-accent"
                            : "border-line-strong",
                        )}
                        aria-hidden="true"
                      />
                    ) : null}
                    <span className="min-w-0 flex-1">{option.label}</span>
                    {!hideCodes ? (
                      <TechnicalValue className="shrink-0 text-[0.8rem] text-muted">
                        {option.code}
                      </TechnicalValue>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      </PopoverContent>
    </Popover>
  );
}
